import {
  BaseCard,
  Button,
  ErrorCard,
  LoadingCard,
  Typography,
} from "@platform/design-system";
import { useState } from "react";
import { useSignedInUser } from "@platform/user/client";

import { EditRfpCarriersDrawer } from "../../../../components/editRfpCarriers/drawer";
import { useAnalyticsData } from "../../../../contexts/analyticsContext";
import {
  RfpCarriersResponse,
  useRfpCarriers,
} from "../../../../hooks/useRfpCarriers";
import { CarrierWithContactsByType } from "../../../../types/carrier";
import { getCarriersWithContactsByType } from "../../../../utils/carrier";

import { CardContent } from "./cardContent";
import { CopyAllContactsButton } from "./copyAllContactsButton";

type Props = { rfpId: string };

interface CarrierContactsResponse {
  total: number;
  centroSelectCarrierEnabled: boolean;
  missingSalesRep: boolean;
  connectedCarriers: CarrierWithContactsByType[];
  unconnectedCarriers: CarrierWithContactsByType[];
  unknownCarriers: CarrierWithContactsByType[];
}

function select(resp: RfpCarriersResponse): CarrierContactsResponse {
  const {
    missingSalesRep: missingSalesRepConnected,
    carriers: connectedCarriers,
  } = getCarriersWithContactsByType(resp.connected);
  const {
    missingSalesRep: missingSalesRepUnconnected,
    carriers: unconnectedCarriers,
  } = getCarriersWithContactsByType(resp.unconnected);
  const { missingSalesRep: missingSalesRepUnknown, carriers: unknownCarriers } =
    getCarriersWithContactsByType(resp.unknown);

  const missingSalesRep =
    missingSalesRepConnected ||
    missingSalesRepUnconnected ||
    missingSalesRepUnknown;
  const total =
    connectedCarriers.length +
    unconnectedCarriers.length +
    unknownCarriers.length;

  return {
    total,
    missingSalesRep,
    connectedCarriers,
    unconnectedCarriers,
    unknownCarriers,
    centroSelectCarrierEnabled:
      // if total is greater than 0 (carriers were selected), centro already selected carriers
      total > 0 ? false : resp.centroSelectCarrierEnabled,
  };
}

function RfpCarrierContactsCard({ rfpId }: Props): React.JSX.Element {
  const [rfpCarriers, setRfpCarriers] = useState<
    CarrierWithContactsByType[] | null
  >(null);
  const { dispatch } = useAnalyticsData();
  const { isBrokerOrAgent } = useSignedInUser();

  const query = useRfpCarriers({
    rfpId,
    select,
    onSuccess: (data) => {
      dispatch({
        carrierConnectedNames: data.connected.map((c) => c.name),
        carrierConnectedCount: data.connected.length,
        carrierUnconnectedNames: data.unconnected.map((c) => c.name),
        carrierUnconnectedCount: data.unconnected.length,
      });
    },
  });

  if (query.isLoading) {
    return (
      <LoadingCard
        title="Carriers"
        gridItemProps={{
          size: { xs: 12, md: 6, lg: 4 },
        }}
        skeleton={{ count: 3, props: { height: 120 } }}
      />
    );
  }

  if (query.isError) {
    return <ErrorCard title="Carriers" error="Failed to load carriers." />;
  }

  const handleEdit = isBrokerOrAgent
    ? undefined
    : () => {
        const allCarriers = query.data.connectedCarriers.concat(
          query.data.unconnectedCarriers,
          query.data.unknownCarriers,
        );
        setRfpCarriers(allCarriers);
      };

  return (
    <>
      <BaseCard
        title="Carriers"
        subtitle={
          query.data.total
            ? `${query.data.total} Total Carriers to Quote`
            : null
        }
        action={
          <>
            <CopyAllContactsButton
              groups={[
                { title: "Connected", carriers: query.data.connectedCarriers },
                {
                  title: "Unconnected",
                  carriers: query.data.unconnectedCarriers,
                },
                { title: "Unknown", carriers: query.data.unknownCarriers },
              ]}
            />
            {isBrokerOrAgent ? null : (
              <Button variant="tertiary" onClick={handleEdit}>
                Edit
              </Button>
            )}
          </>
        }
      >
        {query.data.centroSelectCarrierEnabled ? (
          <Typography>
            <em>Centro to select carriers</em>
          </Typography>
        ) : (
          <CardContent
            missingSalesRep={query.data.missingSalesRep}
            connectedCarriers={query.data.connectedCarriers}
            unconnectedCarriers={query.data.unconnectedCarriers}
            unknownCarriers={query.data.unknownCarriers}
          />
        )}
      </BaseCard>
      {rfpCarriers ? (
        <EditRfpCarriersDrawer
          rfpId={rfpId}
          carriers={rfpCarriers}
          onClose={() => setRfpCarriers(null)}
          onSuccess={() => query.refetch()}
        />
      ) : null}
    </>
  );
}

export { RfpCarrierContactsCard };
